"use client";

import { c as _c } from "react/compiler-runtime";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { PlusIcon } from "lucide-react";
import Link from "next/link";
import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/accordion";
import { DEV_PLAN_DAILY_PERCENT, DEV_PLAN_INCLUDED_RESET_PASSES, DEV_PLAN_PREMIUM_WEEKLY_PERCENT, DEV_PLAN_PRICES, DEV_PLAN_RESET_PASS_PRICES, getDevPlanCreditsLimit, HIGH_COST_INPUT_PRICE, HIGH_COST_OUTPUT_PRICE, MARKETING_STATS, SELF_REFUND_USAGE_PERCENT, SELF_REFUND_WINDOW_DAYS, type DevPlanTier } from "@llmgateway/shared";
import type { ReactNode } from "react";
interface FaqItem {
  question: string;
  // Plain-text answer used for the FAQPage JSON-LD schema (and as the
  // rendered fallback when no rich `content` is provided).
  answer: string;
  content?: ReactNode;
}
export type FaqCredits = Record<DevPlanTier, number>;
const premiumInputPerM = Math.round(HIGH_COST_INPUT_PRICE * 1_000_000);
const premiumOutputPerM = Math.round(HIGH_COST_OUTPUT_PRICE * 1_000_000);
const pct = (fraction: number) => Math.round(fraction * 100);

// Server pages pass the allowances they computed so the rendered FAQ and its
// JSON-LD agree with the plan cards; the fallback serves any other caller.
const defaultCredits: FaqCredits = {
  lite: getDevPlanCreditsLimit("lite"),
  pro: getDevPlanCreditsLimit("pro"),
  max: getDevPlanCreditsLimit("max")
};
function buildFaqData(credits: FaqCredits): FaqItem[] {
  const usagePerDollar = credits.lite / DEV_PLAN_PRICES.lite;
  return [{
    question: "How much usage do I get on each plan?",
    answer: `Every plan includes a fixed monthly usage allowance, calculated at standard provider rates. Lite ($${DEV_PLAN_PRICES.lite}/mo) includes $${credits.lite} in model usage, Pro ($${DEV_PLAN_PRICES.pro}/mo) includes $${credits.pro}, and Max ($${DEV_PLAN_PRICES.max}/mo) includes $${credits.max} — ${usagePerDollar}× the subscription price.`,
    content: <>
					<p>
						Every plan includes a fixed monthly usage allowance, calculated at
						standard provider rates:
					</p>
					<ul className="list-disc pl-6 mt-2 space-y-1">
						<li>
							<strong>Lite — ${DEV_PLAN_PRICES.lite}/mo:</strong> $
							{credits.lite} in monthly model usage
						</li>
						<li>
							<strong>Pro — ${DEV_PLAN_PRICES.pro}/mo:</strong> ${credits.pro}{" "}
							in monthly model usage
						</li>
						<li>
							<strong>Max — ${DEV_PLAN_PRICES.max}/mo:</strong> ${credits.max}{" "}
							in monthly model usage
						</li>
					</ul>
					<p className="mt-3">
						That&apos;s {usagePerDollar}× the subscription price — model access
						at a discount compared to paying providers directly, with a bill
						that never moves.
					</p>
				</>
  }, {
    question: "What is the daily pacing allowance?",
    answer: `Each rolling 24-hour window can use up to ${pct(DEV_PLAN_DAILY_PERCENT.lite)}% of your monthly allowance on Lite, ${pct(DEV_PLAN_DAILY_PERCENT.pro)}% on Pro and ${pct(DEV_PLAN_DAILY_PERCENT.max)}% on Max, across every model — so your allowance lasts the whole cycle instead of draining in a single burst. When a window fills up, further requests are declined until it rolls over; pay-as-you-go overflow lifts it, and upgrading raises it. The window starts with your first request and resets 24 hours later.`,
    content: <>
					<p>
						Each rolling 24-hour window can use a published share of your
						monthly allowance, across every model, so the allowance lasts the
						whole cycle instead of draining in a single burst:
					</p>
					<ul className="list-disc pl-6 mt-2 space-y-1">
						<li>
							<strong>Lite:</strong> {pct(DEV_PLAN_DAILY_PERCENT.lite)}% of
							monthly credits per day
						</li>
						<li>
							<strong>Pro:</strong> {pct(DEV_PLAN_DAILY_PERCENT.pro)}% of
							monthly credits per day
						</li>
						<li>
							<strong>Max:</strong> {pct(DEV_PLAN_DAILY_PERCENT.max)}% of
							monthly credits per day
						</li>
					</ul>
					<p className="mt-3">
						When a window fills up, further requests are declined until it rolls
						over — the window starts with your first request and resets 24 hours
						later. <strong>Pay-as-you-go overflow</strong> lifts it, and
						upgrading raises it.
					</p>
				</>
  }, {
    question: "How is usage calculated?",
    answer: "Usage is metered at each provider's published per-token rate (input, output, and cached tokens). You can see the dollar value of every request in your dashboard in real time."
  }, {
    question: "What happens if I hit my monthly limit?",
    answer: "By default requests pause until your allowance resets at the start of the next billing cycle. You have two ways to keep coding: enable pay-as-you-go overflow in your dashboard — an opt-in that bills extra usage from a credits balance at the same provider rates once your allowance is gone — or upgrade to a higher tier at any time: an immediate upgrade charges the new tier's full price, restarts your billing cycle, and grants the new tier's full monthly allowance right away, plus anything left of your current allowance rolls over. Credits only roll over on an immediate upgrade, never at a regular renewal.",
    content: <>
					<p>
						By default requests pause until your allowance resets at the start
						of the next billing cycle. You have two ways to keep coding:
					</p>
					<p className="mt-3">
						<strong>Enable pay-as-you-go overflow</strong> in your dashboard —
						an opt-in that bills extra usage from a credits balance at the same
						provider rates, only once your allowance is gone. Your plan stays a
						hard cap until you turn it on.
					</p>
					<p className="mt-3">
						Or <strong>upgrade to a higher tier at any time</strong>: an
						immediate upgrade charges the new tier&apos;s full price, restarts
						your billing cycle, and grants the new tier&apos;s full monthly
						allowance right away — plus anything left of your current allowance
						rolls over. Credits only roll over on an immediate upgrade, never at
						a regular renewal.
					</p>
				</>
  }, {
    question: "Can I change plans anytime?",
    answer: "Yes. Upgrades can take effect immediately — you're charged the new tier's full price, your billing cycle restarts, and you get the new tier's full allowance right away, plus any unspent credits from the cycle being replaced roll over until your next renewal — or you can schedule the upgrade for your next renewal instead. Downgrades take effect at your next renewal. There's no lock-in and no cancellation fee."
  }, {
    question: "Do I need a subscription, or is there pay-as-you-go?",
    answer: `Both work. DevPass plans turn every dollar into $${usagePerDollar} of model usage. If you'd rather not subscribe, LLM Gateway offers pay-as-you-go: top up credits and pay per token at provider rates with a flat ${MARKETING_STATS.platformFee} platform fee, or bring your own provider keys for free. Pay-as-you-go organizations are metadata only by default; Enterprise organizations can optionally enable full data retention, billed at ${MARKETING_STATS.dataStoragePrice}. DevPass is metadata only and has no retention option.`
  }, {
    question: "Can my team or company use DevPass?",
    answer: "No. DevPass is intended for private, personal use by individual developers — one developer, one account. It can't be purchased or shared as a team or company, and there are no team plans. For teams and companies, use LLM Gateway's pay-as-you-go product instead, and reach out to contact@llmgateway.io for custom solutions and volume discounts.",
    content: <>
					<p>
						No. DevPass is intended for{" "}
						<strong>private, personal use by individual developers</strong> —
						one developer, one account. It can&apos;t be purchased or shared as
						a team or company, and there are no team plans.
					</p>
					<p className="mt-3">
						For teams and companies, use LLM Gateway&apos;s pay-as-you-go
						product instead — and reach out to{" "}
						<Link href="mailto:contact@llmgateway.io" className="underline">
							contact@llmgateway.io
						</Link>{" "}
						for custom solutions and volume discounts.
					</p>
				</>
  }, {
    question: "Which tools and SDKs work with DevPass?",
    answer: "Anything that speaks the OpenAI or Anthropic API — Claude Code, GitHub Copilot, Empryo, SoulForge, Cursor, Cline, Continue, Aider, the OpenAI and Anthropic SDKs, and more. Set two environment variables and you're in."
  }, {
    question: "Are all 100+ models included on every plan?",
    answer: "Yes. Every plan includes the full catalog — Claude, GPT-5, Gemini, GLM, Qwen, and the rest. Plans differ in the size of your monthly usage allowance, its daily pacing, and the weekly fair-use allowance on premium frontier models."
  }, {
    question: "Can I pin a specific provider, like on pay-as-you-go?",
    answer: `No — DevPass always smart-routes. You request a model by its plain id (e.g. claude-sonnet-5) and the gateway picks the best provider in real time based on uptime, speed, price, and prompt caching — that routing is part of how DevPass stretches every dollar into $${usagePerDollar} of usage. Provider-prefixed model ids like openai/gpt-4o aren't available on DevPass; your coding sessions still stick to one provider automatically to keep prompt caches warm. If you need to pin an exact provider or region, use LLM Gateway's pay-as-you-go API on llmgateway.io, which fully supports provider pinning.`,
    content: <>
					<p>
						No — DevPass always smart-routes. You request a model by its plain
						id (e.g. <code className="font-mono text-sm">claude-sonnet-5</code>)
						and the gateway picks the best provider in real time based on
						uptime, speed, price, and prompt caching — that routing is part of
						how DevPass stretches every dollar into ${usagePerDollar} of usage.
						Provider-prefixed model ids like{" "}
						<code className="font-mono text-sm">openai/gpt-4o</code> aren&apos;t
						available on DevPass, and your coding sessions still stick to one
						provider automatically to keep prompt caches warm.
					</p>
					<p className="mt-3">
						If you need to pin an exact provider or region, use{" "}
						<Link href="https://llmgateway.io" className="underline">
							LLM Gateway&apos;s pay-as-you-go API
						</Link>{" "}
						instead — it fully supports{" "}
						<Link href="https://docs.llmgateway.io/features/routing#provider-specific-routing" className="underline">
							provider-specific routing
						</Link>
						.
					</p>
				</>
  }, {
    question: "Are there limits on premium models?",
    answer: `Premium models — any model priced at $${premiumInputPerM}+ per million input tokens or $${premiumOutputPerM}+ per million output tokens — are subject to a weekly fair-use allowance in addition to your monthly allowance and its daily pacing: ${pct(DEV_PLAN_PREMIUM_WEEKLY_PERCENT.lite)}% of your monthly credits on Lite, ${pct(DEV_PLAN_PREMIUM_WEEKLY_PERCENT.pro)}% on Pro, and ${pct(DEV_PLAN_PREMIUM_WEEKLY_PERCENT.max)}% on Max. Every other model draws on your full monthly allowance. The exact numbers are published on the plan cards — no hidden throttling.`,
    content: <>
					<p>
						Premium models — any model priced at ${premiumInputPerM}+ per
						million input tokens or ${premiumOutputPerM}+ per million output
						tokens — are subject to a weekly fair-use allowance in addition to
						your monthly allowance and its daily pacing:
					</p>
					<ul className="list-disc pl-6 mt-2 space-y-1">
						<li>
							<strong>Lite:</strong> {pct(DEV_PLAN_PREMIUM_WEEKLY_PERCENT.lite)}
							% of monthly credits per week
						</li>
						<li>
							<strong>Pro:</strong> {pct(DEV_PLAN_PREMIUM_WEEKLY_PERCENT.pro)}%
							of monthly credits per week
						</li>
						<li>
							<strong>Max:</strong> {pct(DEV_PLAN_PREMIUM_WEEKLY_PERCENT.max)}%
							of monthly credits per week
						</li>
					</ul>
					<p className="mt-3">
						Every other model draws on your full monthly allowance. The exact
						numbers are published on the plan cards — no hidden throttling. See{" "}
						<Link href="https://docs.llmgateway.io/learn/model-categories" className="underline">
							model categories &amp; fair use
						</Link>{" "}
						for how models are classified.
					</p>
				</>
  }, {
    question: "What if I hit the weekly premium allowance mid-week?",
    answer: `Redeem a Reset Pass: it instantly restores your full weekly premium allowance and starts a fresh 7-day window. A pass removes the weekly limit only — it doesn't add credits, so usage still draws from your monthly allowance. Max includes ${DEV_PLAN_INCLUDED_RESET_PASSES.max} passes per billing cycle; on every tier, extra passes are a one-time purchase from your dashboard ($${DEV_PLAN_RESET_PASS_PRICES.lite} on Lite, $${DEV_PLAN_RESET_PASS_PRICES.pro} on Pro, $${DEV_PLAN_RESET_PASS_PRICES.max} on Max). Standard models keep working the whole time, and if you're resetting every week, upgrading a tier is usually the better deal.`
  }, {
    question: "Does the weekly premium allowance reset when my plan renews?",
    answer: "Yes. Every monthly renewal resets everything at once: your monthly credits, your weekly premium allowance (a fresh 7-day window starts at renewal), and your included Reset Passes. Between renewals the window is rolling — when 7 days end, the next window starts with your next premium request, and redeeming a Reset Pass clears the window so the next request starts a fresh one. There's no reset schedule to manage — neither the weekly window nor unused included passes carry into the next cycle. Purchased Reset Passes are separate: they persist until redeemed.",
    content: <>
					<p>
						Yes. Every monthly renewal resets everything at once: your monthly
						credits, your weekly premium allowance (a fresh 7-day window starts
						at renewal), and your included Reset Passes.
					</p>
					<p className="mt-3">
						Between renewals the window is rolling — when 7 days end, the next
						window starts with your next premium request, and redeeming a Reset
						Pass clears the window so the next request starts a fresh one.
						There&apos;s no reset schedule to manage — neither the weekly window
						nor unused included passes carry into the next cycle. Purchased
						Reset Passes are separate: they persist until redeemed. See{" "}
						<Link href="https://docs.llmgateway.io/learn/reset-passes" className="underline">
							Reset Passes
						</Link>{" "}
						for the full mechanics.
					</p>
				</>
  }, {
    question: "Can I get a refund?",
    answer: `Yes — DevPass comes with a first-month guarantee, and you don't need to email anyone. If you've used less than ${SELF_REFUND_USAGE_PERCENT}% of your monthly allowance, open Billing in your dashboard and hit Refund on the charge: your first month comes back in full, up to ${SELF_REFUND_WINDOW_DAYS} days after the purchase. The refund ends your plan immediately, and there's no cancellation fee.`,
    content: <>
					Yes — DevPass comes with a <strong>first-month guarantee</strong>, and
					you don&apos;t need to email anyone. If you&apos;ve used less than{" "}
					{SELF_REFUND_USAGE_PERCENT}% of your monthly allowance, open{" "}
					<Link href="/dashboard/billing" className="underline">
						Billing
					</Link>{" "}
					in your dashboard and hit <strong>Refund</strong> on the charge: your
					first month comes back in full, up to {SELF_REFUND_WINDOW_DAYS} days
					after the purchase. The refund ends your plan immediately, and
					there&apos;s no cancellation fee.
				</>
  }];
}
export function Faq(t0) {
  const $ = _c(40);
  const {
    credits: t1
  } = t0;
  const credits = t1 === undefined ? defaultCredits : t1;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== credits) {
    const faqData = buildFaqData(credits);
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqData.map(_temp)
    };
    t11 = "w-full py-20 md:py-32 bg-background";
    t12 = "faq";
    t13 = <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />;
    t10 = "container mx-auto px-4 md:px-6";
    t8 = "grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16";
    let t14;
    let t15;
    let t16;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-4">FAQ</p>;
      t15 = <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">Common questions</h2>;
      t16 = <p className="mt-4 text-muted-foreground">Everything you need to know about usage limits, plans, and getting started with DevPass.</p>;
      $[14] = t14;
      $[15] = t15;
      $[16] = t16;
    } else {
      t14 = $[14];
      t15 = $[15];
      t16 = $[16];
    }
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="lg:col-span-2 lg:sticky lg:top-24 lg:self-start">{t14}{t15}{t16}<p className="mt-6 text-sm text-muted-foreground">Can't find an answer?{" "}<Link href="mailto:contact@llmgateway.io" className="text-foreground underline underline-offset-4">Contact us</Link></p></div>;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t7 = "lg:col-span-3";
    T0 = Accordion;
    t2 = "single";
    t3 = true;
    t4 = "w-full";
    t5 = "item-1";
    t6 = faqData.map(_temp2);
    $[0] = credits;
    $[1] = T0;
    $[2] = t10;
    $[3] = t11;
    $[4] = t12;
    $[5] = t13;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    T0 = $[1];
    t10 = $[2];
    t11 = $[3];
    t12 = $[4];
    t13 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t14;
  if ($[18] !== T0 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t14 = <T0 type={t2} collapsible={t3} className={t4} defaultValue={t5}>{t6}</T0>;
    $[18] = T0;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14 || $[26] !== t7) {
    t15 = <div className={t7}>{t14}</div>;
    $[25] = t14;
    $[26] = t7;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15 || $[29] !== t8 || $[30] !== t9) {
    t16 = <div className={t8}>{t9}{t15}</div>;
    $[28] = t15;
    $[29] = t8;
    $[30] = t9;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t10 || $[33] !== t16) {
    t17 = <div className={t10}>{t16}</div>;
    $[32] = t10;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t13 || $[38] !== t17) {
    t18 = <section className={t11} id={t12}>{t13}{t17}</section>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  return t18;
}
function _temp2(item_0, idx) {
  return <AccordionItem key={item_0.question} value={`item-${idx + 1}`} className="py-5 border-border/50"><AccordionPrimitive.Header className="flex"><AccordionPrimitive.Trigger className="focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-center justify-between gap-4 rounded-md py-2 text-left font-display text-lg md:text-xl font-medium leading-7 transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&>svg>path:last-child]:origin-center [&>svg>path:last-child]:transition-all [&>svg>path:last-child]:duration-200 [&[data-state=open]>svg]:rotate-180 [&[data-state=open]>svg>path:last-child]:rotate-90 [&[data-state=open]>svg>path:last-child]:opacity-0 text-foreground">{item_0.question}<PlusIcon size={18} className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200" aria-hidden="true" /></AccordionPrimitive.Trigger></AccordionPrimitive.Header><AccordionContent className="overflow-hidden transition-all data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up text-base text-muted-foreground leading-relaxed pb-2"><div className="border-l-2 border-foreground/10 pl-4">{item_0.content ?? item_0.answer}</div></AccordionContent></AccordionItem>;
}
function _temp(item) {
  return {
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer
    }
  };
}